import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { useNavigationBlock } from "../../../hooks/useNavigationBlock";
import { ReactComponent as RulesetIcon } from "../../../assets/ruleset.svg";
import { NAVBAR_HEIGHT } from "../../../components/Navbar/NavDesktop";
import { usePortal } from "../../../context/PortalsContext";
import { useAuth } from "../../../context/AuthContext";
import { ReactComponent as Alert } from "../../../assets/alert-triangle.svg";
import { ReactComponent as AlertCircle } from "../../../assets/alert-circle.svg";
import {
  addUpdateAssignment,
  createUpdateAssignmentVersion,
  deleteAssignmentVersion,
  getAssignment,
  getDdlAssignments,
  getS3PresignedUrl,
} from "../../../services/api/apiClient";
import {
  AddUpdateAssignmentResult,
  AssignmentDependency,
  AssignmentVersion,
  AssignmentVersionGridRow,
  CreateUpdateAssignmentVersionResult,
  DdlAssignment,
  GetAssignmentResult,
  AssignmentCreateUpdateBodyParams,
  AssignmentType,
  PackageType,
  Status,
} from "../../../services/api/types/assignments/assignment";
import {
  GetS3PresignedUrlResult,
  PresignFileType,
} from "../../../services/api/types/portalConfiguration/uploadPhoto";
import { brand, error, warning } from "../../../theme/themePrimitives";
import { MainAdminForm } from "./components/MainAdminForm";
import { UploadFile } from "./components/UploadFile";
import { UploadSection } from "./components/UploadSection";
import { VersionDetails, VersionField } from "./components/VersionDetails";
import { VersionHistoryGrid } from "./components/VersionHistoryGrid";
import { EditVersionModal } from "./components/EditVersionModal";
import { ClientAdminForm } from "./components/ClientAdminForm";
import { useToast } from "../../../context/ToastContext";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import { extractErrorMessage } from "../../../utils/errorUtils";
import { getFileNameFromUrl } from "../../../utils/helpers";
import { Roles } from "../../../services/api/types/users/users";

interface AssignmentUploadsProps {}

type InputChangeEvent = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>;
export type ChangeEventType = InputChangeEvent | SelectChangeEvent;

export interface FormValues {
  assignmentType: AssignmentType | undefined;
  packageType: PackageType | undefined;
  title: string;
  titleShort: string;
  maxAttempts?: number;
  SKU: string;
  code: string;
  version: string;
  dependency: AssignmentDependency[];
  status: Status | undefined;
  note: string;
  tags?: string[];
  packageFile: File | null;
  thumbnailFile: File | null;
  isAvailableToSubportals: boolean;
  pageByPage: boolean;
  acknowledgement: boolean;
  mandatory: boolean;
}

export interface FormErrors {
  assignmentType: string;
  packageType: string;
  title: string;
  titleShort: string;
  maxAttempts?: string;
  SKU: string;
  code: string;
  version: string;
  dependency: string;
  status: string;
  note: string;
  tags: string;
  packageFile: string;
  thumbnailFile: string;
  isAvailableToSubportals: string;
  pageByPage: string;
  acknowledgement: string;
  mandatory: string;
}

export const AssignmentUploads: React.FC<AssignmentUploadsProps> = () => {
  const initialFormState: FormValues = {
    assignmentType: undefined,
    packageType: undefined,
    title: "",
    titleShort: "",
    status: Status.DRAFT,
    maxAttempts: undefined,
    dependency: [],
    SKU: "",
    code: "",
    version: "",
    note: "",
    tags: [],
    packageFile: null,
    thumbnailFile: null,
    isAvailableToSubportals: false,
    pageByPage: false,
    acknowledgement: true,
    mandatory: false,
  };

  const initialErrorsState: FormErrors = {
    assignmentType: "",
    packageType: "",
    title: "",
    titleShort: "",
    maxAttempts: "",
    SKU: "",
    code: "",
    status: "",
    dependency: "",
    version: "",
    note: "",
    tags: "",
    packageFile: "",
    thumbnailFile: "",
    isAvailableToSubportals: "",
    pageByPage: "",
    acknowledgement: "",
    mandatory: "",
  };

  const [loading, setLoading] = useState(false);
  const [dependencyOptions, setDependencyOptions] = useState<DdlAssignment[]>(
    [],
  );
  const [buttonLoading, setButtonLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>();
  const [assignmentVersionUid, setAssignmentVersionUid] = useState<string>();
  const { showToast } = useToast();
  const [formValues, setFormValues] = useState<FormValues>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>(initialErrorsState);
  const [uid, setUid] = useState<string>();
  const [isCustom, setIsCustom] = useState(false);
  const [portalOwner, setPortalOwner] = useState<string | null>(null);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [versionRows, setVersionRows] = useState<AssignmentVersionGridRow[]>(
    [],
  );
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [showNewVersionUpload, setShowNewVersionUpload] = useState(false);
  const [newVersionUid, setNewVersionUid] = useState<string>();
  const [newVersionLoading, setNewVersionLoading] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [editVersionRow, setEditVersionRow] =
    useState<AssignmentVersionGridRow | null>(null);
  // Settings persisted by the last Continue click; unchanged values skip the
  // request when moving between the two steps.
  const [lastSubmittedValues, setLastSubmittedValues] =
    useState<FormValues | null>(null);
  // Set after a successful save so the delayed navigation to assignment
  // search isn't intercepted, without flipping the UI back to step 1.
  const [saveCompleted, setSaveCompleted] = useState(false);
  const { auth } = useAuth();
  const isSystemAdmin = Boolean(auth.user?.roles?.includes(Roles.SYSTEM_ADMIN));
  const { portalUid, assignmentUid } = useParams<{
    portalUid: string;
    assignmentUid?: string;
  }>();
  const { portals } = usePortal();
  const isEditMode = Boolean(assignmentUid);
  const navigate = useNavigate();

  const isMainPortal = portals.selectedPortal?.isMain || false;

  // Block navigation when on upload section
  const { showModal, confirmNavigation, cancelNavigation } = useNavigationBlock(
    showUploadSection && !saveCompleted,
  );

  // Read portalOwner from localStorage on mount/refresh
  useEffect(() => {
    const storedPortalOwner = localStorage.getItem("portalOwner");
    if (storedPortalOwner) {
      setPortalOwner(storedPortalOwner);
    }
  }, []);

  const handleChange = (e: ChangeEventType) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleVersionChange = (field: VersionField, value: string) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    const fetchDdlAssignments = async () => {
      if (!portalUid) return;
      try {
        const options = await getDdlAssignments(
          portalUid,
          isEditMode && assignmentUid
            ? { assignmentId: assignmentUid }
            : undefined,
        );
        setDependencyOptions(options);
      } catch (err: any) {
        console.log("Error while fetching DDL dependency options:", err);
        showToast(
          extractErrorMessage(err, "Failed to fetch dependency options"),
          "error",
        );
      }
    };
    fetchDdlAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUid, assignmentUid, isEditMode]);

  // Backend responses may still use the legacy PascalCase key — read both
  // until the response rename lands.
  const versionUidOf = (v?: AssignmentVersion | null) =>
    v?.assignmentVersionUid ?? v?.AssignmentVersionUid;

  const mapVersionRows = (
    versions: AssignmentVersion[],
    packageType?: PackageType,
  ): AssignmentVersionGridRow[] => {
    // PDF packages are uploaded as a flat file, so the file name is the last
    // URL segment. SCORM packages are extracted into a folder, so the file
    // name is the second-to-last segment (the last is the launch file).
    const nameSegmentIndex =
      packageType === PackageType.PdfUpload ||
      packageType === PackageType.VideoUpload
        ? 1
        : 2;
    return versions.map((v) => ({
      assignmentVersionUid: versionUidOf(v) ?? "",
      packageName: v.url ? getFileNameFromUrl(v.url, nameSegmentIndex) : null,
      version: v.version ?? "-",
      note: v.note ?? null,
      usersAssigned: v.assignedUsers ?? null,
      creationDate: v.createdAt ?? null,
      isLatest: v.isLatest,
    }));
  };

  const loadAssignment = async (initialLoad = true) => {
    if (!assignmentUid || !isEditMode || !portalUid) return;
    try {
      if (initialLoad) setLoading(true);

      const response: GetAssignmentResult = await getAssignment(
        portalUid,
        assignmentUid,
      );
      if (initialLoad) {
        setFormValues({
          assignmentType: response.assignmentType,
          packageType: response.packageType,
          title: response.title,
          titleShort: response.titleShort,
          SKU: response.SKU,
          status: response.status,
          code: response.code,
          version: "",
          note: "",
          tags: response.tags ?? [],
          packageFile: null,
          thumbnailFile: null,
          isAvailableToSubportals: response.isAvailableToSubportals,
          dependency: response.assignmentDependency ?? [],
          maxAttempts: response.maxAttempts,
          acknowledgement: response.acknowledgement ?? true,
          pageByPage: response.pageByPage ?? false,
          mandatory: response.mandatory ?? false,
        });
        setIsCustom(response.isCustom);
        setThumbnailUrl(response.thumbnail);
        setUid(response.assignmentUid);
      }
      const versions = response.assignmentVersions ?? [];
      const latest =
        versions.find((v) => v.isLatest) ?? versions[versions.length - 1];
      setAssignmentVersionUid(versionUidOf(latest));
      setVersionRows(mapVersionRows(versions, response.packageType));
    } catch (error: any) {
      showToast(
        extractErrorMessage(error, "Failed to fetch assignment"),
        "error",
      );
    } finally {
      if (initialLoad) setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentUid, isEditMode, portalUid]);

  // When navigating back to the base "assignment-uploads" route (no assignmentUid),
  // ensure the form is reset so previous edit data is not shown.
  useEffect(() => {
    if (!isEditMode) {
      setFormValues(initialFormState);
      setErrors(initialErrorsState);
      setThumbnailUrl(undefined);
      setAssignmentVersionUid(undefined);
      setUid(undefined);
      setIsCustom(false);
      setShowUploadSection(false);
      setVersionRows([]);
      setShowNewVersionUpload(false);
      setNewVersionUid(undefined);
      setEditVersionRow(null);
      setLastSubmittedValues(null);
      setSaveCompleted(false);
      setCompleteLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  const handleUploadNewVersion = async () => {
    if (newVersionUid) {
      setShowNewVersionUpload(true);
      return;
    }
    if (!assignmentUid || !portals.selectedPortal) return;
    try {
      setNewVersionLoading(true);
      const created: CreateUpdateAssignmentVersionResult =
        await createUpdateAssignmentVersion(
          portals.selectedPortal.portalUid,
          assignmentUid,
          {},
        );
      setNewVersionUid(
        created.assignmentVersionUid ?? created.AssignmentVersionUid,
      );
      setFormValues((prev) => ({ ...prev, version: "", note: "" }));
      setShowNewVersionUpload(true);
    } catch (err: any) {
      showToast(
        extractErrorMessage(err, "Failed to start a new version"),
        "error",
      );
    } finally {
      setNewVersionLoading(false);
    }
  };

  const handleSaveVersionEdit = async (
    row: AssignmentVersionGridRow,
    version: string,
    note: string,
  ) => {
    if (!assignmentUid || !portals.selectedPortal) return;
    try {
      await createUpdateAssignmentVersion(
        portals.selectedPortal.portalUid,
        assignmentUid,
        {
          assignmentVersionUid: row.assignmentVersionUid,
          version,
          note,
        },
      );
      setEditVersionRow(null);
      showToast("Version updated successfully", "success");
      await loadAssignment(false);
    } catch (err: any) {
      showToast(extractErrorMessage(err, "Failed to update version"), "error");
    }
  };

  const handleRemoveVersion = async (row: AssignmentVersionGridRow) => {
    if (!assignmentUid || !portals.selectedPortal || !row.assignmentVersionUid)
      return;
    try {
      setVersionsLoading(true);
      const response: AssignmentVersion | null = await deleteAssignmentVersion(
        portals.selectedPortal.portalUid,
        assignmentUid,
        row.assignmentVersionUid,
      );
      const newLatestUid = versionUidOf(response);
      if (newLatestUid) {
        setAssignmentVersionUid(newLatestUid);
      }
      showToast("Version removed successfully", "success");
      await loadAssignment(false);
    } catch (err: any) {
      showToast(extractErrorMessage(err, "Failed to remove version"), "error");
    } finally {
      setVersionsLoading(false);
    }
  };

  const formatFieldLabel = (field: string): string => {
    if (field === "title") return "Primary title";
    // Split camelCase into words
    const withSpaces = field.replace(/([a-z])([A-Z])/g, "$1 $2");
    // Capitalize first letter
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  const validateForm = (
    values: FormValues,
    options?: { clientEditMode?: boolean; requireVersion?: boolean },
  ): FormErrors => {
    const clientEditMode = options?.clientEditMode ?? false;
    const requireVersion = options?.requireVersion ?? false;

    const requiredFields: (keyof FormValues)[] = clientEditMode
      ? ["title"]
      : [
          "assignmentType",
          "packageType",
          "title",
          ...(!isCustom ? (["SKU"] as const) : []),
          "code",
          ...(requireVersion ? (["version"] as const) : []),
        ];
    const newErrors: FormErrors = {
      assignmentType: "",
      packageType: "",
      title: "",
      titleShort: "",
      SKU: "",
      status: "",
      code: "",
      version: "",
      note: "",
      tags: "",
      packageFile: "",
      thumbnailFile: "",
      isAvailableToSubportals: "",
      dependency: "",
      pageByPage: "",
      acknowledgement: "",
      mandatory: "",
    };
    requiredFields.forEach((field) => {
      const value = values[field];
      if (
        (typeof value === "string" && value.trim() === "") ||
        value === undefined
      ) {
        newErrors[field] = `${formatFieldLabel(field)} is required`;
      }
    });
    return newErrors;
  };

  const cleanObject = <T extends Record<string, any>>(obj: T): T => {
    return Object.fromEntries(
      Object.entries(obj).filter(([key, value]) => {
        if (value === null) return false;
        if (typeof value === "string" && value.trim() === "") return false;
        if (
          Array.isArray(value) &&
          value.length === 0 &&
          key !== "assignmentDependency"
        )
          return false;
        return true;
      }),
    ) as T;
  };

  const uploadPackage = async (
    file: File,
    assignmentId: string,
    versionId: string,
  ) => {
    const presign: GetS3PresignedUrlResult = await getS3PresignedUrl(
      portals.selectedPortal?.portalUid!,
      {
        assignmentId,
        assignmentVersionId: versionId,
        fileName: file.name,
        contentType: file.type,
        fileType: PresignFileType.PACKAGE,
        assignmentType: formValues.assignmentType,
      },
    );
    await axios.put(presign.uploadUrl, file, {
      headers: { "Content-Type": file.type },
    });
    return presign.path;
  };

  const uploadThumbnail = async (file: File, assignmentId: string) => {
    const presign: GetS3PresignedUrlResult = await getS3PresignedUrl(
      portals.selectedPortal?.portalUid!,
      {
        assignmentId,
        fileName: file.name,
        contentType: file.type,
        fileType: PresignFileType.PACKAGE_THUMBNAIL,
        assignmentType: formValues.assignmentType,
      },
    );
    await axios.put(presign.uploadUrl, file, {
      headers: { "Content-Type": file.type },
    });
    return presign.path;
  };

  const usingNewVersion =
    Boolean(newVersionUid) &&
    (Boolean(formValues.packageFile) ||
      formValues.version.trim() !== "" ||
      formValues.note.trim() !== "");

  const isFormBusy =
    uploadLoading || buttonLoading || completeLoading || newVersionLoading;

  const handleSave = async () => {
    try {
      const clientEditMode =
        isEditMode &&
        !loading &&
        !!portals.selectedPortal &&
        !portals.selectedPortal.isMain &&
        !isCustom;

      const validationErrors = validateForm(formValues, {
        clientEditMode,
        requireVersion: isEditMode && !clientEditMode && usingNewVersion,
      });
      setErrors(validationErrors);
      const hasErrors = Object.values(validationErrors).some(
        (msg) => msg !== "",
      );
      if (hasErrors) {
        return;
      }

      if (!isEditMode) {
        if (
          lastSubmittedValues &&
          JSON.stringify(formValues) === JSON.stringify(lastSubmittedValues)
        ) {
          setShowUploadSection(true);
          return;
        }
        if (!auth.user || !portalUid) return;

        setButtonLoading(true);
        const {
          assignmentType,
          packageType,
          version,
          note,
          packageFile,
          thumbnailFile,
          isAvailableToSubportals,
          dependency,
          ...settings
        } = formValues;

        if (!uid) {
          const created: AddUpdateAssignmentResult = await addUpdateAssignment(
            portalUid,
            cleanObject({
              assignmentType,
              packageType,
              isAvailableToSubportals,
              creatorPortalUid: auth.user.PortalUid,
              ...settings,
            }),
          );
          setUid(created.assignmentUid);
          setAssignmentVersionUid(created.assignmentVersionUid);
        } else {
          await addUpdateAssignment(
            portalUid,
            cleanObject({
              assignmentUid: uid,
              assignmentVersionUid,
              isAvailableToSubportals,
              assignmentDependency: dependency,
              ...settings,
            }),
          );
        }
        setLastSubmittedValues(formValues);
        setShowUploadSection(true);
        return;
      }

      if (usingNewVersion && !formValues.packageFile) {
        setErrors((prev) => ({
          ...prev,
          packageFile: "You must upload a package",
        }));
        return;
      }

      // The update endpoint requires a version uid; without one the body
      // would silently omit the field and fail confusingly on the backend.
      if (
        isEditMode &&
        !(usingNewVersion ? newVersionUid : assignmentVersionUid)
      ) {
        showToast(
          "Could not resolve the assignment version — please reload the page and try again",
          "error",
        );
        return;
      }

      if (auth.user && portalUid && assignmentUid) {
        setButtonLoading(true);
        const {
          assignmentType,
          note,
          version,
          packageType,
          isAvailableToSubportals,
          thumbnailFile,
          packageFile,
          dependency,
          pageByPage,
          acknowledgement,
          ...rest
        } = formValues;

        const pdfFields =
          packageType === PackageType.PdfUpload ? { pageByPage } : {};

        const acknowledgementValue =
          packageType === PackageType.PdfUpload ||
          packageType === PackageType.VideoUpload
            ? acknowledgement
            : undefined;

        const rawBody: AssignmentCreateUpdateBodyParams = {
          assignmentUid,
          assignmentVersionUid: usingNewVersion
            ? newVersionUid
            : assignmentVersionUid,
          ...(usingNewVersion ? { version, note } : {}),
          isAvailableToSubportals: isAvailableToSubportals,
          thumbnail: thumbnailUrl,
          assignmentDependency: dependency,
          pageByPage: pdfFields.pageByPage,
          acknowledgement: acknowledgementValue,
          ...rest,
        };

        await addUpdateAssignment(portalUid, cleanObject(rawBody));

        showToast("Assignment saved successfully", "success");
        setTimeout(() => {
          navigate(
            `/portal-configuration/${portals.selectedPortal?.portalUid}/assignment-search`,
          );
        }, 2000);
      }
    } catch (err: any) {
      const message = extractErrorMessage(err, "Failed to save assignment");
      if (
        typeof message === "string" &&
        message.toLowerCase().includes("sku")
      ) {
        setErrors((prev) => ({ ...prev, SKU: message }));
      } else {
        showToast(message, "error");
      }
    } finally {
      setButtonLoading(false);
    }
  };

  const handleFileUpload = async (file: File | null, type: PresignFileType) => {
    if (type === PresignFileType.PACKAGE) {
      setErrors((prev) => ({ ...prev, packageFile: "" }));
      const targetVersionUid = isEditMode
        ? newVersionUid
        : assignmentVersionUid;
      if (file && uid && targetVersionUid) {
        const originalPackageFile = formValues.packageFile;
        try {
          setUploadLoading(true);
          await uploadPackage(file, uid, targetVersionUid);
          setFormValues((prev) => ({ ...prev, packageFile: file }));
        } catch (err: any) {
          setFormValues((prev) => ({
            ...prev,
            packageFile: originalPackageFile,
          }));
          showToast(
            extractErrorMessage(err, "Failed to upload package"),
            "error",
          );
        } finally {
          setUploadLoading(false);
        }
        return;
      }
      setFormValues((prev) => ({ ...prev, packageFile: file }));
      return;
    }

    if (type !== PresignFileType.PACKAGE_THUMBNAIL) return;

    setErrors((prev) => ({ ...prev, thumbnailFile: "" }));
    if (!file || !uid) {
      setFormValues((prev) => ({ ...prev, thumbnailFile: file }));
      return;
    }

    const originalThumbnailUrl = thumbnailUrl;
    const originalThumbnailFile = formValues.thumbnailFile;
    try {
      setUploadLoading(true);
      const path = await uploadThumbnail(file, uid);
      setThumbnailUrl(path);
      setFormValues((prev) => ({ ...prev, thumbnailFile: file }));
    } catch (err: any) {
      setThumbnailUrl(originalThumbnailUrl);
      setFormValues((prev) => ({
        ...prev,
        thumbnailFile: originalThumbnailFile,
      }));
      showToast(
        extractErrorMessage(err, "Failed to upload thumbnail"),
        "error",
      );
    } finally {
      setUploadLoading(false);
    }
  };

  const handleSaveAssignment = async () => {
    try {
      if (!auth.user || !portalUid) return;

      if (!formValues.packageFile) {
        setErrors((prev) => ({
          ...prev,
          packageFile: "You must upload a package",
        }));
        return;
      }
      if (!formValues.thumbnailFile) {
        setErrors((prev) => ({
          ...prev,
          thumbnailFile: "You must upload a thumbnail",
        }));
        return;
      }
      if (!formValues.version.trim()) {
        setErrors((prev) => ({
          ...prev,
          version: "Version is required",
        }));
        return;
      }

      if (!uid || !assignmentVersionUid) {
        showToast(
          "Assignment draft is missing — please go back and try again",
          "error",
        );
        return;
      }

      setCompleteLoading(true);

      const body: AssignmentCreateUpdateBodyParams = {
        assignmentUid: uid,
        status: Status.ACTIVE,
        thumbnail: thumbnailUrl,
        assignmentVersionUid,
        version: formValues.version,
        note: formValues.note,
        assignmentDependency: formValues.dependency,
        titleShort: formValues.titleShort,
        maxAttempts: formValues.maxAttempts,
      };
      await addUpdateAssignment(portalUid, body);
      setSaveCompleted(true);
      showToast("Assignment saved successfully", "success");
      setTimeout(() => {
        navigate(
          `/portal-configuration/${portals.selectedPortal?.portalUid}/assignment-search`,
        );
      }, 2000);
    } catch (err: any) {
      setCompleteLoading(false);
      const message = extractErrorMessage(err, "Failed to save assignment");
      if (
        typeof message === "string" &&
        message.toLowerCase().includes("sku")
      ) {
        setErrors((prev) => ({ ...prev, SKU: message }));
        setShowUploadSection(false);
      } else {
        showToast(message, "error");
      }
    }
  };

  usePageMetadata(
    {
      title: isEditMode ? "Edit assignment upload" : "Uploads",
      breadcrumbs: [
        {
          label: "Assignment management",
          path: `/portal-configuration/${portalUid}/assignment-search`,
        },
        // if it's edit mode, show the assignment search breadcrumb
        ...(isEditMode
          ? [
              {
                label: "Assignment search",
                path: `/portal-configuration/${portalUid}/assignment-search`,
              },
            ]
          : []),
        {
          label: isEditMode ? "Edit assignment" : "Assignment uploads",
        },
      ],
    },
    [portalUid, isEditMode],
  );

  const showClientForm = useMemo(() => {
    // Only show client form if we have all the necessary data loaded
    // In edit mode, wait for loading to complete and ensure we have selectedPortal
    if (!isEditMode || !portals.selectedPortal || loading) {
      return false;
    }
    // Only show client form for non-main portals when assignment is not custom
    return !portals.selectedPortal.isMain && !isCustom;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    portals.selectedPortal?.portalUid,
    portals.selectedPortal?.isMain,
    isCustom,
    loading,
  ]);

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: `calc(100vh - ${NAVBAR_HEIGHT})`,
          }}
        >
          <CircularProgress size={100} />
        </Box>
      ) : (
        <>
          <Paper
            sx={{
              p: 2,
              ...(isFormBusy && {
                pointerEvents: "none",
              }),
            }}
          >
            <Stack spacing={2}>
              {!showUploadSection && (
                <>
                  <Box
                    sx={
                      isEditMode
                        ? {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }
                        : {}
                    }
                  >
                    <Typography variant="h5">Assignment settings</Typography>
                    {isEditMode && !isMainPortal && (
                      <Tooltip title="Manage Rules">
                        <span
                          style={{
                            cursor:
                              formValues.status === Status.INACTIVE
                                ? "not-allowed"
                                : undefined,
                          }}
                        >
                          <IconButton
                            disabled={formValues.status === Status.INACTIVE}
                            onClick={() =>
                              navigate(
                                `/portal-configuration/${portalUid}/assignment-search/${assignmentUid}/rules`,
                              )
                            }
                            sx={{
                              border: "1px solid #D0D5DD",
                              backgroundColor: "#FFFFFF",
                              borderRadius: "8px",
                              width: "40px",
                              height: "40px",
                              "&:hover": {
                                backgroundColor: "#FFFFFF",
                              },
                              "&.Mui-disabled": {
                                backgroundColor: "#F2F4F7",
                                borderColor: "#EAECF0",
                                cursor: "not-allowed",
                              },
                            }}
                          >
                            <RulesetIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                  <Divider />
                </>
              )}
              {showUploadSection ? (
                <UploadSection
                  errors={errors}
                  formValues={formValues}
                  handleFileUpload={handleFileUpload}
                  onVersionChange={handleVersionChange}
                  setShowUploadSection={setShowUploadSection}
                  loading={uploadLoading}
                  buttonLoading={completeLoading}
                  handleSave={handleSaveAssignment}
                />
              ) : (
                <>
                  {showClientForm ? (
                    <ClientAdminForm
                      handleSave={handleSave}
                      formValues={formValues}
                      errors={errors}
                      handleChange={handleChange}
                      buttonLoading={buttonLoading}
                      isEditMode={isEditMode}
                      setFormValues={setFormValues}
                      portalOwner={portalOwner}
                      dependencyOptions={dependencyOptions}
                    />
                  ) : (
                    <MainAdminForm
                      handleSave={handleSave}
                      formValues={formValues}
                      errors={errors}
                      handleChange={handleChange}
                      setFormValues={setFormValues}
                      buttonLoading={buttonLoading}
                      isEditMode={isEditMode}
                      isCustom={isCustom}
                      dependencyOptions={dependencyOptions}
                    />
                  )}
                </>
              )}
              {(portals.selectedPortal?.isMain || isCustom) && isEditMode ? (
                <>
                  <Divider sx={{ marginTop: "0 !important" }} />
                  <Box sx={{ width: "100%", minWidth: 0 }}>
                    <UploadFile
                      value={formValues.thumbnailFile}
                      onChange={(file) =>
                        handleFileUpload(
                          file,
                          PresignFileType.PACKAGE_THUMBNAIL,
                        )
                      }
                      error={errors.thumbnailFile}
                      url={thumbnailUrl}
                      mode="thumbnail"
                    />
                  </Box>

                  <Divider sx={{ marginTop: "0 !important" }} />

                  <Box sx={{ display: "flex", alignItems: "end", gap: 1 }}>
                    <Typography variant="h5">Package versions</Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      {"(Manage versions and upload new package files)"}
                    </Typography>
                  </Box>

                  <VersionHistoryGrid
                    rows={versionRows}
                    loading={versionsLoading}
                    onRemove={handleRemoveVersion}
                    onEditVersion={setEditVersionRow}
                    onUploadNewVersion={handleUploadNewVersion}
                    uploadNewVersionLoading={newVersionLoading}
                    canRemoveVersions={isSystemAdmin}
                  />

                  <Box
                    sx={{
                      backgroundColor: warning[50],
                      border: `1px solid ${warning[200]}`,
                      borderRadius: "12px",
                      p: 2,
                    }}
                  >
                    <Typography variant="body2" color={warning[700]}>
                      <Box component="span" fontWeight="bold">
                        <AlertCircle style={{ marginRight: "0.5rem" }} />
                        Re-uploading will create a new version:
                      </Box>{" "}
                      Existing learners will continue on the version they were
                      originally assigned, while new assignees will start on the
                      latest version.
                    </Typography>
                  </Box>

                  {showNewVersionUpload && (
                    <>
                      <Box sx={{ width: "100%", minWidth: 0 }}>
                        <UploadFile
                          value={formValues.packageFile}
                          onChange={(file) =>
                            handleFileUpload(file, PresignFileType.PACKAGE)
                          }
                          error={errors.packageFile}
                          label="Upload new version"
                          mode="package"
                          disabled={uploadLoading}
                          packageType={formValues.packageType}
                        />
                      </Box>
                      <Box sx={{ width: "100%", minWidth: 0 }}>
                        <VersionDetails
                          version={formValues.version}
                          note={formValues.note}
                          onChange={handleVersionChange}
                          versionError={errors.version}
                          noteError={errors.note}
                        />
                      </Box>
                    </>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {buttonLoading ? (
                      <CircularProgress />
                    ) : (
                      <Button
                        onClick={() =>
                          usingNewVersion
                            ? setShowSaveConfirm(true)
                            : handleSave()
                        }
                        sx={{
                          px: 2,
                          borderRadius: "12px",
                          backgroundColor: `${brand[600]} !important`,
                          color: `white`,
                          "&:hover": {
                            backgroundColor: `${brand[700]} !important`,
                          },
                        }}
                      >
                        Save assignment
                      </Button>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  {isEditMode &&
                  !portals.selectedPortal?.isMain &&
                  !isCustom ? (
                    <Box>
                      <Box width={"100%"}>
                        <UploadFile
                          value={formValues.thumbnailFile}
                          onChange={(file) =>
                            handleFileUpload(
                              file,
                              PresignFileType.PACKAGE_THUMBNAIL,
                            )
                          }
                          error={errors.thumbnailFile}
                          url={thumbnailUrl}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          mt: 2,
                        }}
                      >
                        {buttonLoading ? (
                          <CircularProgress />
                        ) : (
                          <Button
                            onClick={handleSave}
                            sx={{
                              px: 2,
                              borderRadius: "12px",
                              backgroundColor: `${brand[600]} !important`,
                              color: `white`,
                            }}
                          >
                            <Typography>Save assignment</Typography>
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ) : null}
                </>
              )}
            </Stack>
          </Paper>
        </>
      )}
      <ConfirmModal
        openConfirmModal={showModal}
        setOpenConfirmModal={(open) => {
          if (!open) cancelNavigation();
        }}
        confirmButtonClick={confirmNavigation}
        modalTitle="Assignment not saved"
        modalSubtitle="Do you want to navigate away without saving?"
        icon={
          <Alert style={{ color: error[600], width: "60px", height: "60px" }} />
        }
        cancelButtonText="No, stay here"
        confirmButtonText="Yes, I'm sure"
      />
      <ConfirmModal
        openConfirmModal={showSaveConfirm}
        setOpenConfirmModal={setShowSaveConfirm}
        confirmButtonClick={() => {
          setShowSaveConfirm(false);
          handleSave();
        }}
        modalTitle="Save assignment"
        modalSubtitle="You are about to save a new version of this assignment. New assignees will receive the latest version, while existing learners will continue on the version originally assigned to them. Are you sure you want to proceed?"
        cancelButtonText="No, I don't want"
        confirmButtonText="Yes, I'm sure"
      />
      <EditVersionModal
        row={editVersionRow}
        onClose={() => setEditVersionRow(null)}
        onSave={handleSaveVersionEdit}
      />
    </>
  );
};
